// ============================================
// CALENDARIOCAMPO.JS — Calendario de campo (CALENDARIO Entrega 2)
// ============================================
// Agenda por tecnico: cada tecnico/cuadrilla es una FILA, los dias en columnas.
// Vista Semana (default en PC) y Dia (default en celular). Bloques de color por
// tipo que abarcan su duracion (pueden cruzar varios dias). Semaforo de carga,
// marcar tecnico "falto" (bloques en rojo por reasignar), reasignar arrastrando
// o con boton, detalle del bloque (reprogramar/hecho/cancelar/ver expediente) y
// "+ Programar". Se alimenta de /api/programaciones (backend E2).

import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import {
  Calendar, ChevronLeft, ChevronRight, Plus, X, MapPin, Users, Clock,
  Check, AlertTriangle, UserX, ExternalLink, GripVertical, RefreshCw, FolderOpen, Repeat, Undo2,
} from 'lucide-react';
import { HardHat } from 'lucide-react';
// Bloque 3: etiquetas del estado POR PERSONA de la cuadrilla externa.
const ESTADO_PERSONA = { propuesto: 'propuesto', aceptado: 'aceptado', rechazado: 'rechazado', sin_respuesta: 'sin respuesta', en_curso: 'en curso', terminado: 'terminado', cerrado: 'cerrado' };
import { DndContext, DragOverlay, useDraggable, useDroppable } from '@dnd-kit/core';
import { useSensoresDnD } from './components/PersonalizarMenu';
import { API_BASE_EXP, PP_EXP } from './components/helpersExpedientes';
import { authHeaders } from './utils/storage';
import { useAnchoViewport } from './hooks/useResponsive';

// Expedientes se carga en diferido (import dinamico) para romper el ciclo de
// imports (Expedientes -> CalendarioCampo). Se usa como overlay de SELECCION.
const ExpedientesLazy = lazy(() => import('./Expedientes'));

// ---- Colores y catalogos ----
const TIPOS = {
  levantamiento: { label: 'Levantamiento', color: '#2f5f9e', bg: '#eaf2fb' },
  instalacion:   { label: 'Instalación',   color: '#1D9E75', bg: '#e8faf5' },
  servicio:      { label: 'Servicio',      color: '#8b5cf6', bg: '#f3efff' },
  reparacion:    { label: 'Reparación',    color: '#f59e0b', bg: '#fff8ed' },
  otro:          { label: 'Otro',          color: '#6b7280', bg: '#f1f2f4' },
};
const ESTADOS = {
  programado: { label: 'Programado', color: '#5c9adb' },
  en_curso:   { label: 'En curso',   color: '#1D9E75' },
  hecho:      { label: 'Hecho',      color: '#10b981' },
  reagendado: { label: 'Reagendado', color: '#a86b1e' },
  cancelado:  { label: 'Cancelado',  color: '#e05c5c' },
};
const COL = { principal: '#5c9adb', oscuro: '#2f5f9e', gris: '#4e5357', grisClaro: '#8a8f95', borde: '#e6e8ea', rojo: '#e05c5c', fondoRojo: '#fde8e8' };

// ---- Fechas en zona MX (sin DST desde 2022 => UTC-6 fijo) ----
const MX_TZ = 'America/Mexico_City';
const MX_OFFSET = '-06:00';
const DIAS_CORTO = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function ymd(date) { return date.toLocaleDateString('en-CA', { timeZone: MX_TZ }); }        // 'YYYY-MM-DD' en MX
function hoyYMD() { return ymd(new Date()); }
function addDays(ymdStr, n) { const d = new Date(ymdStr + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }
function mondayOf(ymdStr) { const d = new Date(ymdStr + 'T12:00:00Z'); const dow = (d.getUTCDay() + 6) % 7; d.setUTCDate(d.getUTCDate() - dow); return d.toISOString().slice(0, 10); }
function partesDia(ymdStr) { const d = new Date(ymdStr + 'T12:00:00Z'); return { dow: DIAS_CORTO[d.getUTCDay()], dia: d.getUTCDate(), mes: MESES[d.getUTCMonth()] }; }
function horaMX(iso) { if (!iso) return ''; return new Date(iso).toLocaleTimeString('es-MX', { timeZone: MX_TZ, hour: '2-digit', minute: '2-digit', hour12: true }); }
function fechaHoraMX(iso) { if (!iso) return ''; const d = new Date(iso); const p = partesDia(ymd(d)); return `${p.dow} ${p.dia} ${p.mes}, ${horaMX(iso)}`; }
function toLocalInput(iso) { if (!iso) return ''; const d = new Date(iso); const f = d.toLocaleDateString('en-CA', { timeZone: MX_TZ }); const h = d.toLocaleTimeString('en-GB', { timeZone: MX_TZ, hour: '2-digit', minute: '2-digit' }); return `${f}T${h}`; }
function fromLocalInput(str) { return str ? `${str}:00${MX_OFFSET}` : null; }

// ---- fetch con token (authHeaders de utils/storage: fuente unica del token) ----
async function api(path, opts = {}) {
  const res = await fetch(`${API_BASE_EXP}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  let data = null;
  try { data = await res.json(); } catch (e) { /* respuesta sin json */ }
  if (!res.ok) throw new Error((data && data.error) || `Error ${res.status}`);
  return data;
}

const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${COL.borde}`, fontSize: 16, fontFamily: PP_EXP, minHeight: 44, boxSizing: 'border-box', color: COL.gris, background: '#fff' };
const btnPrimario = { background: COL.principal, color: '#fff', border: 'none', borderRadius: 10, padding: '11px 16px', fontSize: 15, fontWeight: 600, fontFamily: PP_EXP, cursor: 'pointer', minHeight: 44, minWidth: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background 160ms ease, opacity 160ms ease' };
const btnSec = { ...btnPrimario, background: '#fff', color: COL.gris, border: `1px solid ${COL.borde}` };
// Boton de cerrar (X): area tactil >=44x44 en todos los modales.
const btnCerrar = { background: 'none', border: 'none', cursor: 'pointer', color: COL.grisClaro, padding: 8, minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, transition: 'background 160ms ease' };

// ============================================
// Componente principal
// ============================================
export default function CalendarioCampo({ esCelular }) {
  // Umbral del proyecto (<1100 = compacto): PC vertical/tablet caen en vista Dia
  // por defecto y densidad compacta, para no desbordar (regla useAnchoViewport).
  const anchoVp = useAnchoViewport();
  const compacto = esCelular || anchoVp < 1100;
  // Recuerda dia/vista entre navegaciones (para volver al MISMO lugar desde el expediente).
  const guardado = (() => { try { return JSON.parse(sessionStorage.getItem('beep_cal_estado') || 'null'); } catch (e) { return null; } })();
  const [vista, setVista] = useState((guardado && guardado.vista) || (compacto ? 'dia' : 'semana'));
  const [ancla, setAncla] = useState((guardado && guardado.ancla) || hoyYMD());   // dia de referencia
  useEffect(() => { try { sessionStorage.setItem('beep_cal_estado', JSON.stringify({ vista, ancla })); } catch (e) { /* ignora */ } }, [vista, ancla]);
  const [tecnicos, setTecnicos] = useState([]);
  const [programaciones, setProgramaciones] = useState([]);
  const [ausencias, setAusencias] = useState([]);  // [{usuario_id, fecha, motivo}]
  const [filtroTipo, setFiltroTipo] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [detalle, setDetalle] = useState(null);     // programacion abierta
  const [programando, setProgramando] = useState(null); // {} para abrir modal crear
  const [arrastrando, setArrastrando] = useState(null);

  // Dias visibles segun la vista.
  const dias = useMemo(() => {
    if (vista === 'dia') return [ancla];
    const l = mondayOf(ancla);
    return Array.from({ length: 7 }, (_, i) => addDays(l, i));
  }, [vista, ancla]);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    const desde = `${dias[0]}T00:00:00${MX_OFFSET}`;
    const hasta = `${dias[dias.length - 1]}T23:59:59${MX_OFFSET}`;
    const params = new URLSearchParams({ desde, hasta });
    if (filtroTipo) params.set('tipo', filtroTipo);
    // Los tecnicos se cargan de forma INDEPENDIENTE: si la lista de bloques falla,
    // las filas de tecnicos igual aparecen (no mostrar "no hay tecnicos" por error).
    if (!tecnicos.length) {
      try { const tec = await api('/api/programaciones/tecnicos'); setTecnicos(tec.tecnicos || []); }
      catch (e) { /* no bloquear el calendario por esto */ }
    }
    try {
      const prog = await api(`/api/programaciones?${params}`);
      setProgramaciones(prog.programaciones || []);
      setAusencias(prog.ausencias || []);
    } catch (e) {
      setError(e.message || 'No se pudo cargar el calendario.');
    } finally {
      setCargando(false);
    }
  }, [dias, filtroTipo, tecnicos.length]);

  useEffect(() => { cargar(); }, [cargar]);

  // Fila "Sin asignar" para bloques sin tecnico.
  const filas = useMemo(() => {
    const base = tecnicos.map(t => ({ id: t.id, nombre: t.nombre, externo: t.externo }));
    // Bloque 3: la gente de CUADRILLAS EXTERNAS propuesta en el rango tiene su propia fila ('ext:<integrante>'),
    // misma data que el expediente. No se arrastra nada hacia ellas (se proponen desde el formulario).
    const ext = new Map();
    programaciones.forEach(p => (p.integrantes || []).forEach(g => { if (!['rechazado', 'sin_respuesta'].includes(g.estado) && !ext.has(g.integrante_id)) ext.set(g.integrante_id, { id: `ext:${g.integrante_id}`, nombre: `${g.nombre} · ${g.contratista}`, externo: true, cuadrilla: true }); }));
    return [...base, ...[...ext.values()].sort((a, b) => a.nombre.localeCompare(b.nombre)), { id: 'sin', nombre: 'Sin asignar', externo: false }];
  }, [tecnicos, programaciones]);

  // Indexa bloques por tecnico.
  const porTecnico = useMemo(() => {
    const map = {};
    filas.forEach(f => { map[f.id] = []; });
    programaciones.forEach(p => {
      const tecs = (p.tecnicos || []);
      const ext = (p.integrantes || []).filter(g => !['rechazado', 'sin_respuesta'].includes(g.estado));
      if (tecs.length === 0 && ext.length === 0) { map['sin'] && map['sin'].push(p); }
      else {
        tecs.forEach(t => { if (map[t.id]) map[t.id].push(p); });
        ext.forEach(g => { const k = `ext:${g.integrante_id}`; if (map[k]) map[k].push(p); });
      }
    });
    return map;
  }, [programaciones, filas]);

  const ausenteEse = useCallback((tecId, diaStr) => ausencias.some(a => String(a.usuario_id) === String(tecId) && a.fecha === diaStr), [ausencias]);

  // Bloques de un tecnico en un dia (con marca de rojo si ausente).
  const bloquesDe = useCallback((tecId, diaStr) => {
    const lista = porTecnico[tecId] || [];
    const ausente = ausenteEse(tecId, diaStr);
    return lista
      .filter(p => {
        const di = ymd(new Date(p.fecha_inicio));
        const df = ymd(new Date(p.fecha_fin));
        return di <= diaStr && diaStr <= df;
      })
      .map(p => {
        const di = ymd(new Date(p.fecha_inicio));
        const df = ymd(new Date(p.fecha_fin));
        return { ...p, _esInicio: di === diaStr, _esFin: df === diaStr, _porReasignar: ausente && p.estado !== 'cancelado' && p.estado !== 'hecho' };
      });
  }, [porTecnico, ausenteEse]);

  // ---- Drag & drop: arrastrar bloque -> soltar en otro tecnico = reasignar ----
  // Sensores touch-safe reutilizados (PointerSensor distance:5 + TouchSensor delay:200).
  const sensores = useSensoresDnD();
  const onDragStart = (e) => setArrastrando(e.active?.data?.current?.prog || null);
  const onDragEnd = async (e) => {
    setArrastrando(null);
    const prog = e.active?.data?.current?.prog;
    const overId = e.over?.id;
    if (!prog || !overId || !String(overId).startsWith('tec-')) return;
    const destino = String(overId).slice(4);
    if (destino === 'sin' || destino.startsWith('ext:')) return;   // Bloque 3: a una cuadrilla externa no se arrastra
    const idDestino = Number(destino);
    const yaEsta = (prog.tecnicos || []).some(t => t.id === idDestino);
    if (yaEsta && (prog.tecnicos || []).length === 1) return; // ya es de ese tecnico solo
    const tec = tecnicos.find(t => t.id === idDestino);
    if (!window.confirm(`¿Reasignar "${tituloBloque(prog)}" a ${tec ? tec.nombre : 'este técnico'}?`)) return;
    try {
      await api(`/api/programaciones/${prog.id}/reasignar`, { method: 'PATCH', body: JSON.stringify({ tecnicos: [idDestino] }) });
      cargar();
    } catch (err) { alert(err.message || 'No se pudo reasignar.'); }
  };

  const navegar = (dir) => setAncla(a => addDays(a, dir * (vista === 'dia' ? 1 : 7)));
  const irHoy = () => setAncla(hoyYMD());

  const rangoTexto = vista === 'dia'
    ? (() => { const p = partesDia(dias[0]); return `${p.dow} ${p.dia} ${p.mes}`; })()
    : (() => { const a = partesDia(dias[0]); const b = partesDia(dias[6]); return `${a.dia} ${a.mes} — ${b.dia} ${b.mes}`; })();

  return (
    <div className="calc-root" style={{ fontFamily: PP_EXP, color: COL.gris, padding: esCelular ? '12px' : '20px 24px', maxWidth: '100%', boxSizing: 'border-box' }}>
      <style>{'.calc-root button{transition:background 160ms ease,filter 160ms ease,opacity 160ms ease}.calc-root button:hover:not(:disabled){filter:brightness(0.95)}.calc-root .calc-bloque{transition:filter 160ms ease}.calc-root .calc-bloque:hover{filter:brightness(0.96)}.calc-root .calc-bloque:focus-visible{outline:2px solid #2f5f9e;outline-offset:1px}'}</style>
      {/* Cabecera */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 'auto' }}>
          <Calendar size={22} color={COL.oscuro} />
          <h2 style={{ margin: 0, fontSize: esCelular ? 18 : 22, fontWeight: 700, color: COL.oscuro }}>Calendario de campo</h2>
        </div>
        <button onClick={() => setProgramando({})} style={btnPrimario}><Plus size={18} /> Programar</button>
      </div>

      {/* Controles */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <div style={{ display: 'inline-flex', border: `1px solid ${COL.borde}`, borderRadius: 10, overflow: 'hidden' }}>
          {['semana', 'dia'].map(v => (
            <button key={v} onClick={() => setVista(v)}
              style={{ padding: '9px 14px', minHeight: 44, border: 'none', cursor: 'pointer', fontFamily: PP_EXP, fontSize: 14, fontWeight: 600, background: vista === v ? COL.principal : '#fff', color: vista === v ? '#fff' : COL.gris }}>
              {v === 'semana' ? 'Semana' : 'Día'}
            </button>
          ))}
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <button onClick={() => navegar(-1)} style={{ ...btnSec, padding: '9px 12px' }} aria-label="Anterior"><ChevronLeft size={18} /></button>
          <button onClick={irHoy} style={{ ...btnSec, padding: '9px 14px' }}>Hoy</button>
          <button onClick={() => navegar(1)} style={{ ...btnSec, padding: '9px 12px' }} aria-label="Siguiente"><ChevronRight size={18} /></button>
        </div>
        <span style={{ fontWeight: 700, color: COL.oscuro, fontSize: 15 }}>
          {rangoTexto}
          <span style={{ fontWeight: 500, color: COL.grisClaro, fontSize: 13, marginLeft: 8 }}>· {programaciones.filter(p => p.estado !== 'cancelado').length} trabajo{programaciones.filter(p => p.estado !== 'cancelado').length === 1 ? '' : 's'}{vista === 'dia' ? ' hoy' : ''}</span>
        </span>
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 140, padding: '9px 12px' }}>
          <option value="">Todos los tipos</option>
          {Object.entries(TIPOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={cargar} style={{ ...btnSec, padding: '9px 12px' }} aria-label="Actualizar"><RefreshCw size={16} /></button>
      </div>

      {error && <div style={{ background: COL.fondoRojo, color: COL.rojo, padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 }}>{error}</div>}

      {/* Leyenda de tipos */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12, fontSize: 12, color: COL.grisClaro }}>
        {Object.entries(TIPOS).map(([k, v]) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: v.color, display: 'inline-block' }} /> {v.label}
          </span>
        ))}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: COL.rojo, display: 'inline-block' }} /> Por reasignar (técnico faltó)
        </span>
      </div>

      {cargando ? (
        <div style={{ padding: 40, textAlign: 'center', color: COL.grisClaro }}>Cargando calendario…</div>
      ) : (
       <>
        {tecnicos.length === 0 && (
          <div style={{ background: '#fff8ed', border: '1px solid #fde3bd', color: '#8a5a1e', borderRadius: 10, padding: '12px 14px', marginBottom: 12, fontSize: 13.5, lineHeight: 1.5 }}>
            Aún no hay técnicos registrados. Créalos en <b>Usuarios</b> (rol Técnico) para que aparezcan como filas. Mientras, los trabajos que agendes sin técnico caen en la fila <b>“Sin asignar”</b>.
          </div>
        )}
        <DndContext sensors={sensores} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <Grid
            filas={filas} dias={dias} vista={vista} esCelular={esCelular} compacto={compacto}
            bloquesDe={bloquesDe} ausenteEse={ausenteEse} porTecnico={porTecnico}
            onAbrirBloque={setDetalle}
            onIrADia={(d) => { setVista('dia'); setAncla(d); }}
            onToggleAusencia={async (tecId, diaStr, estaAusente) => {
              try {
                if (estaAusente) await api('/api/programaciones/ausencias', { method: 'DELETE', body: JSON.stringify({ usuario_id: tecId, fecha: diaStr }) });
                else await api('/api/programaciones/ausencias', { method: 'POST', body: JSON.stringify({ usuario_id: tecId, fecha: diaStr }) });
                cargar();
              } catch (e) { alert(e.message); }
            }}
          />
          <DragOverlay>
            {arrastrando ? (
              <div style={{ ...bloqueEstiloBase(arrastrando), boxShadow: '0 6px 18px rgba(0,0,0,0.22)', opacity: 0.95, minWidth: 140 }}>
                <b style={{ fontSize: 12 }}>{tituloBloque(arrastrando)}</b>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
       </>
      )}

      {detalle && (
        <ModalDetalle
          prog={detalle} tecnicos={tecnicos} esCelular={esCelular}
          onCerrar={() => setDetalle(null)}
          onCambio={() => { cargar(); }}
          onCerrarYRecargar={() => { setDetalle(null); cargar(); }}
        />
      )}
      {programando && (
        <ModalProgramar
          tecnicos={tecnicos} esCelular={esCelular} inicial={programando}
          onCerrar={() => setProgramando(null)}
          onCreada={() => { setProgramando(null); cargar(); }}
        />
      )}
    </div>
  );
}

// ---- helpers de bloque ----
function tituloBloque(p) {
  if (p.oportunidad_nombre) return p.oportunidad_nombre;   // titulo del expediente
  if (p.titulo) return p.titulo;
  if (p.oportunidad_folio) return `${p.oportunidad_folio}`;
  return (TIPOS[p.tipo] || TIPOS.otro).label;
}
function subtituloBloque(p) {
  const partes = [];
  if (p.oportunidad_cliente) partes.push(p.oportunidad_cliente);
  else if (p.oportunidad_empresa) partes.push(p.oportunidad_empresa);
  if (p.oportunidad_ubicacion) partes.push(p.oportunidad_ubicacion);
  return partes.join(' · ');
}
function bloqueEstiloBase(p) {
  const t = TIPOS[p.tipo] || TIPOS.otro;
  return { background: t.bg, borderLeft: `4px solid ${t.color}`, borderRadius: 8, padding: '6px 8px', color: COL.gris, fontFamily: PP_EXP };
}

// ============================================
// Linea de tiempo por horas y medidor de carga
// ============================================
// Eje de 24 h: Beep tiene turnos nocturnos (instalaciones en tiendas cerradas,
// bancos). Sin restriccion de "horario laboral".
const HORA_INI = 0, HORA_FIN = 24;
const WIN_INI = HORA_INI * 60, WIN_FIN = HORA_FIN * 60, WIN = WIN_FIN - WIN_INI;
const HORAS = Array.from({ length: HORA_FIN - HORA_INI }, (_, i) => HORA_INI + i);

function minOfDayMX(iso) {
  const s = new Date(iso).toLocaleTimeString('en-GB', { timeZone: MX_TZ, hour: '2-digit', minute: '2-digit' });
  const [h, m] = s.split(':').map(Number); return h * 60 + m;
}
// Posicion/ancho (%) del tramo del bloque DENTRO del dia y de la ventana de horas.
function tramoEnDia(prog, dia) {
  const di = ymd(new Date(prog.fecha_inicio)); const df = ymd(new Date(prog.fecha_fin));
  const startMin = (di === dia) ? minOfDayMX(prog.fecha_inicio) : 0;      // empezo antes -> 00:00
  const endMin = (df === dia) ? minOfDayMX(prog.fecha_fin) : 24 * 60;     // termina despues -> 24:00
  // Clamp a la ventana de horas; un bloque fuera de rango queda como sliver en el borde (visible).
  const a = Math.min(Math.max(startMin, WIN_INI), WIN_FIN - 15);
  const b = Math.min(Math.max(endMin, a + 15), WIN_FIN);
  // Continuacion: viene de un dia ANTERIOR / sigue a un dia POSTERIOR (turnos nocturnos).
  return { left: ((a - WIN_INI) / WIN) * 100, width: ((b - a) / WIN) * 100, fueraIzq: di < dia, fueraDer: df > dia };
}
// Empaqueta bloques en carriles para que los EMPALMES no se encimen (se apilan).
function empacarCarriles(bloques, dia) {
  const items = bloques.map(p => ({ p, tramo: tramoEnDia(p, dia) })).sort((a, b) => a.tramo.left - b.tramo.left);
  const finCarril = [];
  return items.map(it => {
    let carril = finCarril.findIndex(f => f <= it.tramo.left + 0.1);
    if (carril === -1) { carril = finCarril.length; finCarril.push(0); }
    finCarril[carril] = it.tramo.left + it.tramo.width;
    return { ...it, carril, carriles: 0 };
  }).map(it => ({ ...it, carriles: finCarril.length }));
}
// Medidor de carga del periodo mostrado.
function medidorCarga(n) {
  if (n === 0) return { dots: 0, label: 'libre', color: '#9ca3af' };
  if (n <= 2) return { dots: 2, label: 'ligero', color: '#1D9E75' };
  if (n <= 4) return { dots: 3, label: 'medio', color: '#f59e0b' };
  return { dots: 4, label: 'lleno', color: '#e05c5c' };
}

// Etiqueta del tecnico: avatar + nombre + medidor de carga + externo + faltó.
function EtiquetaTecnico({ fila, carga, esSin, ausenteHoy, mostrarFalta, onToggleFalta, ancho }) {
  const m = medidorCarga(carga);
  return (
    <div style={{ width: ancho, flexShrink: 0, position: 'sticky', left: 0, zIndex: 3, borderRight: `1px solid ${COL.borde}`, padding: '8px 10px', boxSizing: 'border-box', background: ausenteHoy ? '#fdecec' : (esSin ? '#fafafa' : '#fff'), display: 'flex', flexDirection: 'column', gap: 5, justifyContent: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {!esSin && <span style={{ width: 30, height: 30, borderRadius: '50%', background: colorDe(fila.nombre), color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{iniciales(fila.nombre)}</span>}
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontWeight: 700, fontSize: 13, color: esSin ? COL.grisClaro : COL.gris, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fila.nombre}</span>
          {fila.externo && <span style={{ fontSize: 9.5, background: '#f3efff', color: '#8b5cf6', padding: '0 5px', borderRadius: 5 }}>Externo</span>}
        </span>
      </div>
      {!esSin && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }} title={`${carga} trabajo(s)`}>
            {[0, 1, 2, 3].map(i => <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: i < m.dots ? m.color : '#e4e6e9' }} />)}
            <span style={{ fontSize: 10.5, color: m.color, fontWeight: 600, marginLeft: 2 }}>{m.label}</span>
          </span>
        </div>
      )}
      {ausenteHoy && <span style={{ fontSize: 10.5, color: COL.rojo, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}><AlertTriangle size={11} /> Faltó hoy</span>}
      {mostrarFalta && !esSin && (
        <button onClick={onToggleFalta} title={ausenteHoy ? 'Quitar ausencia' : 'Marcar que faltó este día'}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, border: `1px solid ${ausenteHoy ? COL.rojo : COL.borde}`, background: ausenteHoy ? '#fff' : '#fff', color: ausenteHoy ? COL.rojo : COL.grisClaro, borderRadius: 8, padding: '6px 8px', fontSize: 11, cursor: 'pointer', fontFamily: PP_EXP, minHeight: 44 }}>
          <UserX size={13} /> {ausenteHoy ? 'Quitar falta' : 'Faltó'}
        </button>
      )}
    </div>
  );
}

// ============================================
// Grid: elige vista Dia (linea de tiempo) o Semana (compacta)
// ============================================
function Grid(props) {
  return props.vista === 'dia' ? <VistaDia {...props} /> : <VistaSemana {...props} />;
}

// ---- VISTA DIA: linea de tiempo por horas ----
// ---- VISTA DIA VERTICAL (tipo Google Calendar): horas al eje izquierdo,
//      cada tecnico una COLUMNA, bloques altos posicionados por hora. ----
const HEADER_H = 92;
function tramoVertical(prog, dia) {
  const di = ymd(new Date(prog.fecha_inicio)); const df = ymd(new Date(prog.fecha_fin));
  const startMin = (di === dia) ? minOfDayMX(prog.fecha_inicio) : 0;
  const endMin = (df === dia) ? minOfDayMX(prog.fecha_fin) : 24 * 60;
  return { startMin, endMin: Math.max(endMin, startMin + 15), continuaArriba: di < dia, continuaAbajo: df > dia };
}
// Empaqueta en carriles VERTICALES: bloques que se cruzan van lado a lado en la columna.
// Empaqueta en carriles considerando el ALTO MINIMO renderizado (minMin en minutos):
// dos bloques mas cercanos que ese minimo van LADO A LADO (no se enciman visualmente).
function empacarVertical(bloques, dia, minMin) {
  const items = bloques.map(p => ({ p, tr: tramoVertical(p, dia) })).sort((a, b) => a.tr.startMin - b.tr.startMin);
  const finLane = [];
  const conLane = items.map(it => {
    const efEnd = Math.max(it.tr.endMin, it.tr.startMin + (minMin || 0));
    let lane = finLane.findIndex(f => f <= it.tr.startMin);
    if (lane === -1) { lane = finLane.length; finLane.push(0); }
    finLane[lane] = efEnd;
    return { ...it, lane };
  });
  return { items: conLane, lanes: finLane.length || 1 };
}

function VistaDia({ filas, dias, esCelular, compacto, bloquesDe, ausenteEse, porTecnico, onAbrirBloque, onToggleAusencia }) {
  const dia = dias[0];
  // Horas altas para que hasta un trabajo de 30 min mida >=44px sin encimar al vecino.
  const hourH = compacto ? 76 : 88;
  const anchoHoras = 46;
  const colW = compacto ? 150 : 176;
  const totalH = 24 * hourH;
  const scrollRef = React.useRef(null);
  React.useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 7 * hourH; }, [hourH, dia]);
  const esHoy = dia === hoyYMD();
  const ahoraMin = esHoy ? minOfDayMX(new Date().toISOString()) : null;
  const ahoraTxt = ahoraMin != null ? `${String(Math.floor(ahoraMin / 60)).padStart(2, '0')}:${String(ahoraMin % 60).padStart(2, '0')}` : '';
  return (
    <div ref={scrollRef} style={{ overflow: 'auto', border: `1px solid ${COL.borde}`, borderRadius: 12, maxHeight: `min(${compacto ? '80vh' : '76vh'}, ${totalH + HEADER_H + 4}px)` }}>
      <div style={{ display: 'flex', minWidth: anchoHoras + filas.length * colW }}>
        {/* Columna de horas (fija a la izquierda) */}
        <div style={{ width: anchoHoras, flexShrink: 0, position: 'sticky', left: 0, zIndex: 3, background: '#fff', borderRight: `1px solid ${COL.borde}` }}>
          <div style={{ height: HEADER_H, position: 'sticky', top: 0, zIndex: 4, background: '#fafbfc', borderBottom: `1px solid ${COL.borde}` }} />
          <div style={{ position: 'relative', height: totalH }}>
            {HORAS.map(h => <div key={h} style={{ position: 'absolute', top: h * hourH - 6, right: 4, fontSize: 10, color: COL.grisClaro }}>{String(h).padStart(2, '0')}:00</div>)}
          </div>
        </div>
        {/* Columnas de tecnicos */}
        {filas.map(f => (
          <ColumnaTecnico key={f.id} fila={f} dia={dia} colW={colW} hourH={hourH} totalH={totalH} ahoraMin={ahoraMin} ahoraTxt={ahoraTxt}
            carga={(porTecnico[f.id] || []).filter(p => p.estado !== 'cancelado').length}
            bloques={bloquesDe(f.id, dia)} ausenteHoy={f.id !== 'sin' && ausenteEse(f.id, dia)}
            onAbrirBloque={onAbrirBloque} onToggleFalta={() => onToggleAusencia(f.id, dia, ausenteEse(f.id, dia))} />
        ))}
      </div>
    </div>
  );
}

function ColumnaTecnico({ fila, dia, colW, hourH, totalH, ahoraMin, ahoraTxt, carga, bloques, ausenteHoy, onAbrirBloque, onToggleFalta }) {
  const esSin = fila.id === 'sin' || String(fila.id).startsWith('ext:');   // 'ext:' = cuadrilla externa (Bloque 3): sin drop
  const { setNodeRef, isOver } = useDroppable({ id: `tec-${fila.id}`, disabled: esSin });
  const minMin = Math.ceil(54 * 60 / hourH); // alto minimo (~54px) para 3 lineas: tipo+hora+cliente
  const { items, lanes } = useMemo(() => empacarVertical(bloques, dia, minMin), [bloques, dia, minMin]);
  const m = medidorCarga(carga);
  return (
    // flex:1 con base colW -> con pocos tecnicos las columnas llenan el ancho; con muchos, scroll.
    <div style={{ flex: `1 0 ${colW}px`, minWidth: colW, borderRight: `1px solid ${COL.borde}` }}>
      {/* Cabecera de columna (fija arriba) */}
      <div style={{ height: HEADER_H, boxSizing: 'border-box', position: 'sticky', top: 0, zIndex: 2, borderBottom: `1px solid ${COL.borde}`, background: ausenteHoy ? '#fdecec' : '#fafbfc', padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 3, justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {!esSin && <span style={{ width: 26, height: 26, borderRadius: '50%', background: colorDe(fila.nombre), color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{iniciales(fila.nombre)}</span>}
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: 'block', fontWeight: 700, fontSize: 12.5, color: esSin ? COL.grisClaro : COL.gris, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fila.nombre}</span>
            {fila.externo && <span style={{ fontSize: 9, background: '#f3efff', color: '#8b5cf6', padding: '0 5px', borderRadius: 5 }}>Externo</span>}
          </span>
        </div>
        {!esSin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              {ausenteHoy
                ? <span style={{ fontSize: 10, color: COL.rojo, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 2 }}><AlertTriangle size={10} /> Faltó hoy</span>
                : <>{[0, 1, 2, 3].map(i => <span key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: i < m.dots ? m.color : '#e4e6e9' }} />)}<span style={{ fontSize: 10, color: m.color, fontWeight: 600 }}>{m.label}</span></>}
            </span>
            <button onClick={onToggleFalta} title={ausenteHoy ? 'Quitar ausencia' : 'Marcar que faltó hoy'}
              style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${ausenteHoy ? COL.rojo : COL.borde}`, background: '#fff', color: ausenteHoy ? COL.rojo : COL.grisClaro, borderRadius: 8, padding: 6, minHeight: 44, minWidth: 44, cursor: 'pointer' }}><UserX size={15} /></button>
          </div>
        )}
      </div>
      {/* Pista de horas con bloques */}
      <div ref={setNodeRef} style={{ position: 'relative', height: totalH, background: ausenteHoy ? '#fff6f6' : (isOver ? '#eef7ee' : '#fff') }}>
        {HORAS.map(h => <div key={h} style={{ position: 'absolute', top: h * hourH, left: 0, right: 0, height: 1, background: COL.borde, opacity: 0.45 }} />)}
        {ahoraMin != null && (
          <div style={{ position: 'absolute', top: ahoraMin * hourH / 60, left: 0, right: 0, height: 2, background: '#e05c5c', zIndex: 1 }}>
            <span style={{ position: 'absolute', left: 2, top: -9, fontSize: 9, color: '#fff', fontWeight: 700, background: '#e05c5c', padding: '0 3px', borderRadius: 3 }}>{ahoraTxt}</span>
          </div>
        )}
        {items.map(({ p, tr, lane }) => {
          const top = tr.startMin * hourH / 60;
          // Alto = extension REAL del carril (max con el minimo) -> nunca invade al de abajo.
          const height = Math.max(tr.endMin - tr.startMin, minMin) * hourH / 60;
          const anchoPct = 100 / lanes;
          return (
            <BloqueVertical key={`${p.id}-${dia}`} prog={p} dragId={`blk-${p.id}-${dia}`} arrastrable={!esSin} altoPx={height}
              continuaArriba={tr.continuaArriba} continuaAbajo={tr.continuaAbajo}
              style={{ position: 'absolute', top, height: height - 2, left: `calc(${lane * anchoPct}% + 2px)`, width: `calc(${anchoPct}% - 4px)` }}
              onAbrir={() => onAbrirBloque(p)} />
          );
        })}
        {bloques.length === 0 && <div style={{ position: 'absolute', top: 7 * hourH + 6, left: 6, fontSize: 11, color: '#c8ccd0' }}>Sin trabajos</div>}
      </div>
    </div>
  );
}

// Bloque VERTICAL con DENSIDAD ADAPTATIVA: cuenta la historia solo. Segun el alto:
// siempre tipo+hora+cliente/titulo; 1h+ agrega ubicacion; 2h+ agrega folio.
function BloqueVertical({ prog, dragId, arrastrable, style, onAbrir, continuaArriba, continuaAbajo, altoPx }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: dragId, data: { prog }, disabled: !arrastrable });
  const t = TIPOS[prog.tipo] || TIPOS.otro;
  const rojo = prog._porReasignar;
  const cancelado = prog.estado === 'cancelado';
  const hecho = prog.estado === 'hecho';
  const enCurso = prog.estado === 'en_curso';
  const recurrente = !!prog.serie_id;
  const hora = `${horaMX(prog.fecha_inicio)}–${horaMX(prog.fecha_fin)}`;
  const cliente = prog.oportunidad_cliente || prog.oportunidad_empresa || '';
  const titulo = prog.oportunidad_nombre || '';
  const linea = [titulo, cliente].filter(Boolean).join(' · ');
  const alto = altoPx || 44;
  // Tooltip (desktop) con TODO el detalle, para los bloques que no alcanzan a mostrarlo.
  const tooltip = [`${t.label} · ${hora}`, [titulo, cliente].filter(Boolean).join(' · '), prog.oportunidad_ubicacion, prog.oportunidad_folio].filter(Boolean).join('\n');
  const bg = hecho ? '#e9f6ef' : (rojo ? COL.fondoRojo : t.bg);
  const bordeCol = hecho ? '#10b981' : (rojo ? COL.rojo : t.color);
  const tachado = hecho || cancelado;
  return (
    <div ref={setNodeRef} onClick={onAbrir} className="calc-bloque" tabIndex={0} role="button" title={tooltip}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAbrir(); } }}
      style={{ ...style, boxSizing: 'border-box', minHeight: 44, background: bg, border: rojo ? `1.5px dashed ${COL.rojo}` : `1px solid ${bordeCol}55`, borderLeft: `4px solid ${bordeCol}`, boxShadow: enCurso ? `inset 0 0 0 1.5px ${t.color}` : 'none', borderRadius: 8, padding: '3px 6px', cursor: 'pointer', overflow: 'hidden', opacity: isDragging ? 0.35 : (cancelado ? 0.55 : (hecho ? 0.85 : 1)), transition: 'filter 160ms ease, opacity 160ms ease', display: 'flex', flexDirection: 'column', gap: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        {arrastrable && <span {...attributes} {...listeners} onClick={e => e.stopPropagation()} title="Arrastra para reasignar" style={{ cursor: 'grab', touchAction: 'none', color: COL.grisClaro, display: 'inline-flex', padding: 2, margin: -2, flexShrink: 0 }}><GripVertical size={12} /></span>}
        <b style={{ fontSize: 12, lineHeight: 1.15, color: hecho ? '#10b981' : (rojo ? COL.rojo : t.color), textDecoration: tachado ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {continuaArriba && <span title="Viene de ayer">↑ </span>}
          {hecho && <Check size={11} color="#10b981" style={{ marginRight: 1, verticalAlign: 'middle' }} />}{t.label}
          {recurrente && <Repeat size={11} color={hecho ? '#10b981' : t.color} style={{ marginLeft: 3, verticalAlign: 'middle' }} />}
        </b>
      </div>
      <span style={{ fontSize: 10.5, lineHeight: 1.15, color: COL.gris, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: tachado ? 'line-through' : 'none' }}>{hora}</span>
      {linea && <span style={{ fontSize: 10.5, lineHeight: 1.15, color: COL.gris, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: tachado ? 'line-through' : 'none' }}>{linea}</span>}
      {alto >= 78 && prog.oportunidad_ubicacion && <span style={{ fontSize: 10, lineHeight: 1.15, color: COL.grisClaro, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><MapPin size={9} style={{ verticalAlign: 'middle' }} /> {prog.oportunidad_ubicacion}</span>}
      {alto >= 150 && prog.oportunidad_folio && <span style={{ fontSize: 10, lineHeight: 1.15, color: COL.grisClaro, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prog.oportunidad_folio}</span>}
      {(rojo || continuaAbajo) && <span style={{ fontSize: 9.5, color: rojo ? COL.rojo : COL.grisClaro, whiteSpace: 'nowrap' }}>{rojo ? 'por reasignar' : ''}{continuaAbajo ? ' ↓ sigue' : ''}</span>}
    </div>
  );
}

// ---- VISTA SEMANA: panorama compacto ----
function VistaSemana({ filas, dias, esCelular, compacto, bloquesDe, ausenteEse, porTecnico, onAbrirBloque, onIrADia }) {
  const anchoTec = compacto ? 120 : 172;
  const minColDia = compacto ? 128 : 140;
  const total = anchoTec + dias.length * minColDia;
  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${COL.borde}`, borderRadius: 12 }}>
      <div style={{ minWidth: total }}>
        {/* Encabezado de dias */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${COL.borde}`, background: '#fafbfc', position: 'sticky', top: 0, zIndex: 4 }}>
          <div style={{ width: anchoTec, flexShrink: 0, position: 'sticky', left: 0, zIndex: 5, background: '#fafbfc', padding: '10px 12px', fontWeight: 700, fontSize: 12, color: COL.grisClaro, boxSizing: 'border-box', borderRight: `1px solid ${COL.borde}` }}>Técnico</div>
          {dias.map(d => {
            const p = partesDia(d); const esHoy = d === hoyYMD();
            return (
              <div key={d} style={{ width: minColDia, flexShrink: 0, padding: '8px 6px', textAlign: 'center', borderLeft: `1px solid ${COL.borde}`, background: esHoy ? '#eaf2fb' : 'transparent' }}>
                <div style={{ fontSize: 10.5, color: COL.grisClaro, textTransform: 'uppercase' }}>{p.dow}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: esHoy ? COL.oscuro : COL.gris }}>{p.dia}</div>
              </div>
            );
          })}
        </div>
        {filas.map(f => (
          <FilaSemana key={f.id} fila={f} dias={dias} anchoTec={anchoTec} minColDia={minColDia}
            carga={(porTecnico[f.id] || []).filter(p => p.estado !== 'cancelado').length}
            bloquesDe={bloquesDe} ausenteEse={ausenteEse} onAbrirBloque={onAbrirBloque} onIrADia={onIrADia} />
        ))}
      </div>
    </div>
  );
}

function FilaSemana({ fila, dias, anchoTec, minColDia, carga, bloquesDe, ausenteEse, onAbrirBloque, onIrADia }) {
  const esSin = fila.id === 'sin' || String(fila.id).startsWith('ext:');   // 'ext:' = cuadrilla externa (Bloque 3): sin drop
  const { setNodeRef, isOver } = useDroppable({ id: `tec-${fila.id}`, disabled: esSin });
  return (
    <div ref={setNodeRef} style={{ display: 'flex', borderBottom: `1px solid ${COL.borde}`, background: isOver ? '#eef7ee' : '#fff', minHeight: 62 }}>
      <EtiquetaTecnico fila={fila} carga={carga} esSin={esSin} ausenteHoy={false} mostrarFalta={false} ancho={anchoTec} />
      {dias.map(d => {
        const bloques = bloquesDe(fila.id, d);
        const ausente = !esSin && ausenteEse(fila.id, d);
        const visibles = bloques.slice(0, 3);
        const resto = bloques.length - visibles.length;
        return (
          <div key={d} style={{ width: minColDia, flexShrink: 0, borderLeft: `1px solid ${COL.borde}`, padding: 4, background: ausente ? '#fff6f6' : (d === hoyYMD() ? '#f7fbff' : 'transparent'), display: 'flex', flexDirection: 'column', gap: 5 }}>
            {visibles.map(b => (
              <RenglonSemana key={`${b.id}-${d}`} prog={b} dragId={`blk-${b.id}-${d}`} arrastrable={!esSin} onAbrir={() => onAbrirBloque(b)} />
            ))}
            {resto > 0 && (
              <button onClick={() => onIrADia(d)} style={{ border: `1px solid ${COL.borde}`, background: '#fff', color: COL.oscuro, fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'center', padding: '4px 8px', minHeight: 44, borderRadius: 8, fontFamily: PP_EXP }}>+{resto} más</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Renglon compacto de la vista Semana (con hora).
function RenglonSemana({ prog, dragId, arrastrable, onAbrir }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: dragId, data: { prog }, disabled: !arrastrable });
  const t = TIPOS[prog.tipo] || TIPOS.otro;
  const rojo = prog._porReasignar;
  const cancelado = prog.estado === 'cancelado';
  const hecho = prog.estado === 'hecho';
  const multiDia = !prog._esInicio || !prog._esFin;
  const col = hecho ? '#10b981' : (rojo ? COL.rojo : t.color);
  return (
    <div ref={setNodeRef} onClick={onAbrir} className="calc-bloque" tabIndex={0} role="button"
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAbrir(); } }}
      style={{ display: 'flex', alignItems: 'center', gap: 4, background: hecho ? '#e9f6ef' : (rojo ? COL.fondoRojo : t.bg), borderLeft: `3px solid ${col}`, border: rojo ? `1px dashed ${COL.rojo}` : undefined, borderRadius: 6, padding: '4px 6px', cursor: 'pointer', minHeight: 44, opacity: isDragging ? 0.35 : (cancelado ? 0.55 : (hecho ? 0.85 : 1)) }}>
      {arrastrable && <span {...attributes} {...listeners} onClick={e => e.stopPropagation()} style={{ cursor: 'grab', touchAction: 'none', color: COL.grisClaro, display: 'inline-flex', padding: 2, margin: -2, flexShrink: 0 }}><GripVertical size={11} /></span>}
      <span style={{ fontSize: 10.5, color: COL.gris, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: (cancelado || hecho) ? 'line-through' : 'none' }}>
        {hecho && <Check size={10} color="#10b981" style={{ verticalAlign: 'middle', marginRight: 1 }} />}
        {multiDia ? <b style={{ color: col }}>↔ {t.label}</b> : <><b style={{ color: col }}>{prog._esInicio ? horaMX(prog.fecha_inicio) : ''}</b> {t.label}</>}
        {prog.oportunidad_cliente ? ` · ${prog.oportunidad_cliente}` : (prog.oportunidad_empresa ? ` · ${prog.oportunidad_empresa}` : '')}
      </span>
    </div>
  );
}

// ============================================
// Modal de detalle del bloque
// ============================================
function ModalDetalle({ prog, tecnicos, esCelular, onCerrar, onCambio, onCerrarYRecargar }) {
  const [modo, setModo] = useState('ver'); // ver | reasignar | reprogramar
  const [selTec, setSelTec] = useState((prog.tecnicos || []).map(t => t.id));
  const li = toLocalInput(prog.fecha_inicio); const lf = toLocalInput(prog.fecha_fin);
  const [diaIni, setDiaIni] = useState(li.slice(0, 10));
  const [diaFin, setDiaFin] = useState(lf.slice(0, 10));
  const [horaIni, setHoraIni] = useState(li.slice(11, 16));
  const [horaFin, setHoraFin] = useState(lf.slice(11, 16));
  const [progDia, setProgDia] = useState([]); // otros trabajos del dia (sin este bloque) para disponibilidad
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState('');
  const t = TIPOS[prog.tipo] || TIPOS.otro;
  const anchoVp = useAnchoViewport();
  const compacto = esCelular || anchoVp < 1100;
  const esSerie = !!prog.serie_id;
  const [alcance, setAlcance] = useState('solo'); // solo | serie (reprogramar)
  const [cancelando, setCancelando] = useState(false); // pide alcance al cancelar una serie
  const [estado, setEstadoL] = useState(prog.estado); // estado local (refleja al instante)
  const [hist, setHist] = useState([]);
  const ini = `${diaIni}T${horaIni}`;
  const fin = `${diaFin}T${horaFin}`;
  // Historial firmado (para mostrar quien/cuando marco el estado). Se refresca al cambiar estado.
  useEffect(() => {
    let vivo = true;
    (async () => { try { const r = await api(`/api/programaciones/${prog.id}`); if (vivo) setHist(r.historial || []); } catch (e) { /* silencioso */ } })();
    return () => { vivo = false; };
  }, [prog.id, estado]);
  const cambiarEstado = (nuevo) => accion(async () => { await api(`/api/programaciones/${prog.id}/estado`, { method: 'PATCH', body: JSON.stringify({ estado: nuevo }) }); setEstadoL(nuevo); onCambio(); });
  const firmaEstado = hist.find(h => h.tipo_evento === 'estado');
  const aplicarRango = (p) => {
    if (p.diaIni !== undefined) setDiaIni(p.diaIni);
    if (p.diaFin !== undefined) setDiaFin(p.diaFin);
    if (p.horaIni !== undefined) setHoraIni(p.horaIni);
    if (p.horaFin !== undefined) setHoraFin(p.horaFin);
  };

  useEffect(() => {
    if (!diaIni) return undefined;
    let vivo = true;
    (async () => {
      try { const r = await api(`/api/programaciones?desde=${diaIni}T00:00:00${MX_OFFSET}&hasta=${diaFin || diaIni}T23:59:59${MX_OFFSET}`); if (vivo) setProgDia((r.programaciones || []).filter(p => p.id !== prog.id)); }
      catch (e) { if (vivo) setProgDia([]); }
    })();
    return () => { vivo = false; };
  }, [diaIni, diaFin, prog.id]);

  const accion = async (fn) => {
    setGuardando(true); setErr('');
    try { await fn(); } catch (e) { setErr(e.message || 'Error'); setGuardando(false); return; }
    setGuardando(false);
  };

  return (
    <Overlay onCerrar={onCerrar} esCelular={esCelular} ancho={520}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
        <span style={{ width: 14, height: 14, borderRadius: 4, background: t.color, marginTop: 4, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: COL.grisClaro, fontWeight: 600, textTransform: 'uppercase' }}>{t.label} · {(ESTADOS[prog.estado] || {}).label}</div>
          <h3 style={{ margin: '2px 0 0', fontSize: 18, color: COL.oscuro }}>{tituloBloque(prog)}</h3>
        </div>
        <button onClick={onCerrar} aria-label="Cerrar" style={btnCerrar}><X size={22} /></button>
      </div>

      {err && <div style={{ background: COL.fondoRojo, color: COL.rojo, padding: '8px 12px', borderRadius: 8, marginBottom: 10, fontSize: 13 }}>{err}</div>}

      {modo === 'ver' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: (ESTADOS[estado] || {}).color || COL.gris, background: `${(ESTADOS[estado] || {}).color || COL.grisClaro}1a`, borderRadius: 8, padding: '4px 10px' }}>
              {estado === 'hecho' && <Check size={13} />}{estado === 'cancelado' && <X size={13} />}{(ESTADOS[estado] || {}).label || estado}
            </span>
            {esSerie && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#8b5cf6', background: '#f3efff', borderRadius: 8, padding: '4px 10px', fontWeight: 600 }}><Repeat size={13} /> Serie</span>}
          </div>
          {(estado === 'hecho' || estado === 'cancelado' || estado === 'en_curso') && firmaEstado && (
            <div style={{ fontSize: 12, color: COL.grisClaro, marginBottom: 8, marginTop: -2 }}>{firmaEstado.descripcion} — {firmaEstado.usuario_nombre || 'Sistema'} · {firmaEstado.cuando}</div>
          )}
          <Campo icono={<Clock size={16} />} texto={`${fechaHoraMX(prog.fecha_inicio)} → ${fechaHoraMX(prog.fecha_fin)}`} />
          <Campo icono={<Users size={16} />} texto={(prog.tecnicos || []).length ? prog.tecnicos.map(t2 => t2.nombre).join(', ') : 'Sin asignar'} />
          {(prog.integrantes || []).length > 0 && (
            <Campo icono={<HardHat size={16} />} texto={`Cuadrilla externa: ${prog.integrantes.map(g => `${g.nombre} (${g.contratista}) — ${ESTADO_PERSONA[g.estado] || g.estado}${g.motivo ? ': ' + g.motivo : ''}`).join(' · ')}`} />
          )}
          {prog.instrucciones_externo && <Campo icono={<HardHat size={16} />} texto={`Instrucciones para la cuadrilla: ${prog.instrucciones_externo}`} />}
          {/* Datos LEIDOS del expediente (no se re-capturan aqui) */}
          {(prog.oportunidad_cliente || prog.oportunidad_empresa) && <Campo icono={<Users size={16} />} texto={`Cliente: ${prog.oportunidad_cliente || prog.oportunidad_empresa}`} />}
          {prog.oportunidad_ubicacion && <Campo icono={<MapPin size={16} />} texto={`Ubicación: ${prog.oportunidad_ubicacion}`} />}
          {prog.oportunidad_direccion && <Campo icono={<MapPin size={16} />} texto={`Dirección del sitio: ${prog.oportunidad_direccion}`} />}
          {(prog.oportunidad_contacto_nombre || prog.oportunidad_contacto_tel) && <Campo icono={<Users size={16} />} texto={`Contacto: ${[prog.oportunidad_contacto_nombre, prog.oportunidad_contacto_tel].filter(Boolean).join(' · ')}`} />}
          {prog.oportunidad_id && (
            <button
              onClick={() => { window.dispatchEvent(new CustomEvent('beep-abrir-expediente', { detail: { expedienteId: prog.oportunidad_id, retorno: { label: `${t.label} · ${fechaHoraMX(prog.fecha_inicio)}` } } })); onCerrar(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: '#eaf2fb', border: `1px solid ${COL.borde}`, borderRadius: 8, padding: '10px 12px', margin: '8px 0', cursor: 'pointer', fontFamily: PP_EXP, fontSize: 14, color: COL.oscuro, minHeight: 44 }}>
              <ExternalLink size={16} /> Ver expediente {prog.oportunidad_folio || `#${prog.oportunidad_id}`}
            </button>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 18 }}>
            <button onClick={() => setModo('reasignar')} style={btnSec}><Users size={16} /> Reasignar</button>
            <button onClick={() => setModo('reprogramar')} style={btnSec}><Clock size={16} /> Reprogramar</button>
            {estado === 'programado' && <button disabled={guardando} onClick={() => cambiarEstado('en_curso')} style={{ ...btnSec, color: '#1D9E75', borderColor: '#bfe9d6' }}><Clock size={16} /> En curso</button>}
            {estado !== 'hecho' && estado !== 'cancelado' && <button disabled={guardando} onClick={() => cambiarEstado('hecho')} style={{ ...btnSec, color: '#10b981', borderColor: '#bfe9d6' }}><Check size={16} /> Marcar hecho</button>}
            {(estado === 'hecho' || estado === 'en_curso') && <button disabled={guardando} onClick={() => cambiarEstado('programado')} style={btnSec}><Undo2 size={16} /> Deshacer</button>}
            {estado !== 'cancelado' && <button disabled={guardando} onClick={() => { if (esSerie) { setCancelando(true); } else if (window.confirm('¿Cancelar esta programación? Queda registrada como cancelada.')) accion(async () => { await api(`/api/programaciones/${prog.id}`, { method: 'DELETE' }); onCerrarYRecargar(); }); }} style={{ ...btnSec, color: COL.rojo, borderColor: '#f3c4c4' }}><X size={16} /> Cancelar</button>}
          </div>
          {cancelando && (
            <div style={{ background: '#fdecec', border: `1px solid #f3c4c4`, borderRadius: 10, padding: 12, marginTop: 10 }}>
              <div style={{ fontSize: 13.5, color: COL.rojo, fontWeight: 600, marginBottom: 8 }}>Este trabajo es parte de una serie. ¿Qué cancelas?</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button disabled={guardando} onClick={() => accion(async () => { await api(`/api/programaciones/${prog.id}?alcance=solo`, { method: 'DELETE' }); onCerrarYRecargar(); })} style={btnSec}>Solo este trabajo</button>
                <button disabled={guardando} onClick={() => accion(async () => { await api(`/api/programaciones/${prog.id}?alcance=serie`, { method: 'DELETE' }); onCerrarYRecargar(); })} style={{ ...btnPrimario, background: COL.rojo }}>Toda la serie</button>
                <button onClick={() => setCancelando(false)} style={btnSec}>Volver</button>
              </div>
            </div>
          )}
        </>
      )}

      {modo === 'reasignar' && (
        <>
          <p style={{ fontSize: 14, color: COL.gris, marginTop: 0 }}>Selecciona quién atiende este trabajo (uno o varios = cuadrilla):</p>
          <SelectorTecnicosDisponibilidad tecnicos={tecnicos} seleccionados={selTec} onCambio={setSelTec} progDia={progDia} ini={ini} fin={fin} />
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button disabled={guardando} onClick={() => accion(async () => { await api(`/api/programaciones/${prog.id}/reasignar`, { method: 'PATCH', body: JSON.stringify({ tecnicos: selTec }) }); onCambio(); setModo('ver'); })} style={btnPrimario}>Guardar</button>
            <button onClick={() => setModo('ver')} style={btnSec}>Volver</button>
          </div>
        </>
      )}

      {modo === 'reprogramar' && (
        <>
          <RangoFechaHora diaIni={diaIni} diaFin={diaFin} horaIni={horaIni} horaFin={horaFin}
            onCambio={aplicarRango} diasCarga={SET_VACIO} onRangoVisible={NOOP} compacto={compacto} />
          {esSerie && (
            <div style={{ marginTop: 10, background: '#f3efff', border: '1px solid #e4d9fb', borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 12.5, color: '#6b46c1', fontWeight: 600, marginBottom: 6 }}>Este trabajo es parte de una serie. El cambio aplica a:</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[['solo', 'Solo este trabajo'], ['serie', 'Toda la serie (mismo horario)']].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setAlcance(v)} style={{ border: `1px solid ${alcance === v ? COL.principal : COL.borde}`, background: alcance === v ? '#eaf2fb' : '#fff', color: alcance === v ? COL.oscuro : COL.gris, borderRadius: 8, padding: '8px 12px', minHeight: 44, fontSize: 13, cursor: 'pointer', fontFamily: PP_EXP }}>{l}</button>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button disabled={guardando} onClick={() => {
              if (new Date(fromLocalInput(fin)) <= new Date(fromLocalInput(ini))) { setErr('La fecha/hora de fin debe ser posterior al inicio.'); return; }
              accion(async () => { await api(`/api/programaciones/${prog.id}?alcance=${esSerie ? alcance : 'solo'}`, { method: 'PUT', body: JSON.stringify({ fecha_inicio: fromLocalInput(ini), fecha_fin: fromLocalInput(fin) }) }); onCambio(); setModo('ver'); });
            }} style={btnPrimario}>Guardar</button>
            <button onClick={() => setModo('ver')} style={btnSec}>Volver</button>
          </div>
        </>
      )}
    </Overlay>
  );
}

function Campo({ icono, texto }) {
  return <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, fontSize: 14, color: COL.gris }}><span style={{ color: COL.grisClaro, marginTop: 1 }}>{icono}</span><span>{texto}</span></div>;
}
const lbl = { display: 'block', fontSize: 13, fontWeight: 600, color: COL.gris, margin: '10px 0 4px' };

// ---- Avatar (iniciales + color estable por nombre) y disponibilidad ----
const AVATAR_COLORS = ['#5c9adb', '#1D9E75', '#8b5cf6', '#f59e0b', '#e05c5c', '#0ea5e9', '#d97706', '#7c3aed'];
function iniciales(nombre) {
  const partes = String(nombre || '?').trim().split(/\s+/).filter(Boolean);
  return ((partes[0] || '?')[0] + (partes[1] ? partes[1][0] : '')).toUpperCase();
}
function colorDe(nombre) {
  let h = 0; const s = String(nombre || '');
  for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[h];
}
// Disponibilidad de un tecnico para el rango elegido, contra las programaciones de ese dia.
function disponibilidadTec(tecId, progDia, ini, fin) {
  const ci = new Date(fromLocalInput(ini)); const cf = new Date(fromLocalInput(fin));
  const suyos = (progDia || []).filter(p => p.estado !== 'cancelado' && (p.tecnicos || []).some(t => t.id === tecId));
  const emp = suyos.find(p => new Date(p.fecha_inicio) < cf && new Date(p.fecha_fin) > ci);
  if (emp) return { nivel: 'rojo', color: '#e05c5c', texto: `Empalme: ${horaMX(emp.fecha_inicio)}–${horaMX(emp.fecha_fin)}`, orden: 2 };
  if (suyos.length) return { nivel: 'ambar', color: '#c07a12', texto: `Ocupado · ${suyos.length} trabajo${suyos.length > 1 ? 's' : ''} ese día`, orden: 1 };
  return { nivel: 'verde', color: '#1D9E75', texto: 'Libre ese día', orden: 0 };
}

// ============================================
// Selector de RANGO de fechas (estilo reserva de vuelo) + horas
// Componente propio ligero, sin dependencias nuevas (usa helpers MX del archivo).
// ============================================
const NOOP = () => {};
const SET_VACIO = new Set();
const MESES_LARGO = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DOW_MIN = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const HORAS_LAB = (() => { const a = []; for (let h = 0; h <= 23; h++) { a.push(`${String(h).padStart(2, '0')}:00`); a.push(`${String(h).padStart(2, '0')}:30`); } return a; })();

function ymdPartes(ymd) { const [a, m, d] = ymd.split('-').map(Number); return { a, m: m - 1, d }; }
function fechaLargaTxt(ymd) { const { m, d } = ymdPartes(ymd); return `${d} de ${MESES_LARGO[m]}`; }
function diffDias(y1, y2) { return Math.round((new Date(y2 + 'T12:00:00Z') - new Date(y1 + 'T12:00:00Z')) / 86400000); }
function celdasMes(anio, mes) {
  const dow = (new Date(Date.UTC(anio, mes, 1)).getUTCDay() + 6) % 7;
  const dias = new Date(Date.UTC(anio, mes + 1, 0)).getUTCDate();
  const celdas = [];
  for (let i = 0; i < dow; i++) celdas.push(null);
  for (let d = 1; d <= dias; d++) celdas.push(`${anio}-${String(mes + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  while (celdas.length % 7 !== 0) celdas.push(null);
  return celdas;
}
function resumenRango(diaIni, diaFin, horaIni, horaFin) {
  if (!diaIni || !diaFin) return '';
  const n = diffDias(diaIni, diaFin) + 1;
  let fechaTxt;
  if (diaIni === diaFin) fechaTxt = fechaLargaTxt(diaIni);
  else { const p1 = ymdPartes(diaIni); const p2 = ymdPartes(diaFin); fechaTxt = (p1.m === p2.m) ? `Del ${p1.d} al ${fechaLargaTxt(diaFin)}` : `Del ${fechaLargaTxt(diaIni)} al ${fechaLargaTxt(diaFin)}`; }
  // Duracion REAL (soporta turnos que cruzan medianoche: 22:00 -> 02:00 = 4 h).
  const iniDT = new Date(`${diaIni}T${horaIni}:00${MX_OFFSET}`);
  const finDT = new Date(`${diaFin}T${horaFin}:00${MX_OFFSET}`);
  const totalMin = Math.round((finDT - iniDT) / 60000);
  let dur = '';
  if (totalMin > 0 && totalMin < 24 * 60) { const hrs = totalMin / 60; dur = `${Number.isInteger(hrs) ? hrs : hrs.toFixed(1)} hora${hrs === 1 ? '' : 's'}`; }
  else if (totalMin >= 24 * 60) dur = `${n} días`;
  return `${fechaTxt} · ${horaIni} a ${horaFin}${dur ? ` · ${dur}` : ''}`;
}

// ---- Generacion de ocurrencias de una serie recurrente ----
const DOW_LARGO = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']; // 0=Lun
const TOPE_MESES = 12; // tope duro de la serie (12 meses) para no generar infinito
// Suma n meses anclado al dia original; si el mes destino no tiene ese dia (31 en meses cortos),
// cae al ultimo dia del mes (no se "corre" al mes siguiente).
function addMeses(ymd, n) {
  const p = ymdPartes(ymd);
  const ultimo = new Date(Date.UTC(p.a, p.m + n + 1, 0)).getUTCDate();
  const dia = Math.min(p.d, ultimo);
  const dt = new Date(Date.UTC(p.a, p.m + n, dia));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}
function dowDe(ymd) { return (new Date(ymd + 'T12:00:00Z').getUTCDay() + 6) % 7; } // 0=Lun
// Devuelve [{fecha_inicio, fecha_fin}] (ISO con offset MX) segun la regla.
function generarOcurrencias({ diaIni, diaFin, horaIni, horaFin, freq, diasSemana, terminaTipo, terminaN, terminaFecha }) {
  const spanDias = Math.max(0, diffDias(diaIni, diaFin));
  const tope = addMeses(diaIni, TOPE_MESES);
  const limite = (terminaTipo === 'fecha' && terminaFecha) ? (terminaFecha < tope ? terminaFecha : tope) : tope;
  const maxN = terminaTipo === 'nrep' ? Math.max(1, Math.min(terminaN || 1, 366)) : 366;
  const dias = [];
  if (freq === 'no' || !freq) {
    dias.push(diaIni);
  } else if (freq === 'semanal' || freq === 'quincenal') {
    const step = freq === 'semanal' ? 7 : 14;
    let d = diaIni, guard = 0;
    while (dias.length < maxN && d <= limite && guard < 500) { dias.push(d); d = addDays(d, step); guard++; }
  } else if (freq === 'mensual') {
    // Cada mes se calcula desde el dia ORIGINAL + k meses (evita el drift del 31).
    let k = 0;
    while (dias.length < maxN && k < 400) { const d = addMeses(diaIni, k); if (d > limite) break; dias.push(d); k++; }
  } else if (freq === 'personalizado') {
    const sel = (diasSemana && diasSemana.length ? diasSemana : [dowDe(diaIni)]).slice().sort((a, b) => a - b);
    let lunes = mondayOf(diaIni), guard = 0;
    while (dias.length < maxN && lunes <= limite && guard < 80) {
      for (const wd of sel) { const d = addDays(lunes, wd); if (d >= diaIni && d <= limite && dias.length < maxN) dias.push(d); }
      lunes = addDays(lunes, 7); guard++;
    }
  }
  return dias.slice(0, maxN).map(d => ({
    fecha_inicio: `${d}T${horaIni}:00${MX_OFFSET}`,
    fecha_fin: `${addDays(d, spanDias)}T${horaFin}:00${MX_OFFSET}`,
  }));
}
// Cuenta cuantas ocurrencias chocan (empalme) con los trabajos existentes del tecnico.
function ocurrenciasQueChocan(ocurrencias, progExistentes, tecnicos) {
  return ocurrencias.filter(o => {
    const ci = new Date(o.fecha_inicio), cf = new Date(o.fecha_fin);
    return (progExistentes || []).some(p => p.estado !== 'cancelado'
      && (p.tecnicos || []).some(t => tecnicos.includes(t.id))
      && new Date(p.fecha_inicio) < cf && new Date(p.fecha_fin) > ci);
  });
}

function MesCalendario({ anio, mes, diaIni, diaFin, diasCarga, hoy, onClickDia }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 14, color: COL.oscuro, marginBottom: 6, textTransform: 'capitalize' }}>{MESES_LARGO[mes]} {anio}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {DOW_MIN.map((d, i) => <div key={i} style={{ textAlign: 'center', fontSize: 10.5, color: COL.grisClaro, fontWeight: 600, padding: '2px 0' }}>{d}</div>)}
        {celdasMes(anio, mes).map((ymd, i) => {
          if (!ymd) return <div key={i} />;
          const esIni = ymd === diaIni, esFin = ymd === diaFin;
          const extremo = esIni || esFin;
          const entre = diaIni && diaFin && ymd > diaIni && ymd < diaFin;
          const pasado = ymd < hoy;
          const conCarga = diasCarga && diasCarga.has(ymd);
          const { d } = ymdPartes(ymd);
          return (
            <button key={i} type="button" onClick={() => onClickDia(ymd)} title={pasado ? 'Fecha pasada' : undefined}
              style={{ position: 'relative', minHeight: 44, border: 'none', borderRadius: extremo ? 8 : (entre ? 0 : 8), cursor: 'pointer', fontFamily: PP_EXP, padding: 0,
                background: extremo ? COL.principal : (entre ? '#dbe9f8' : 'transparent'),
                color: extremo ? '#fff' : (pasado ? '#c3c7cb' : COL.gris),
                fontWeight: extremo || ymd === hoy ? 700 : 500, fontSize: 13,
                boxShadow: ymd === hoy && !extremo ? `inset 0 0 0 1.5px ${COL.principal}` : 'none' }}>
              {d}
              {conCarga && !extremo && <span title="Este técnico ya tiene carga" style={{ position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)', width: 5, height: 5, borderRadius: '50%', background: '#f59e0b' }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RangoFechaHora({ diaIni, diaFin, horaIni, horaFin, onCambio, diasCarga, onRangoVisible, compacto }) {
  const hoy = hoyYMD();
  const [ancla, setAncla] = useState(() => { const p = ymdPartes(diaIni || hoy); return { a: p.a, m: p.m }; });
  const [esperandoFin, setEsperandoFin] = useState(false);
  const meses = compacto ? 1 : 2;

  useEffect(() => {
    const desde = `${ancla.a}-${String(ancla.m + 1).padStart(2, '0')}-01`;
    const fm = new Date(Date.UTC(ancla.a, ancla.m + meses, 0));
    const hasta = `${fm.getUTCFullYear()}-${String(fm.getUTCMonth() + 1).padStart(2, '0')}-${String(fm.getUTCDate()).padStart(2, '0')}`;
    if (onRangoVisible) onRangoVisible(desde, hasta);
  }, [ancla.a, ancla.m, meses, onRangoVisible]);

  const nav = (delta) => setAncla(prev => { const d = new Date(Date.UTC(prev.a, prev.m + delta, 1)); return { a: d.getUTCFullYear(), m: d.getUTCMonth() }; });

  const clickDia = (ymd) => {
    if (!esperandoFin) { onCambio({ diaIni: ymd, diaFin: ymd }); setEsperandoFin(true); }        // 1er clic = inicio
    else if (ymd < diaIni) { onCambio({ diaIni: ymd, diaFin: ymd }); }                             // anterior = reinicia
    else { onCambio({ diaIni, diaFin: ymd }); setEsperandoFin(false); }                            // 2do clic = fin
  };

  const cambiarHoraIni = (h) => {
    if (diaIni === diaFin) {
      const [ph, pm] = horaIni.split(':').map(Number); const [fh, fm] = horaFin.split(':').map(Number);
      const dur = (fh * 60 + fm) - (ph * 60 + pm);
      const [nh, nm] = h.split(':').map(Number);
      let end = Math.min(nh * 60 + nm + (dur > 0 ? dur : 60), 23 * 60 + 30);
      onCambio({ horaIni: h, horaFin: `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}` });
    } else onCambio({ horaIni: h });
  };

  const meses2 = Array.from({ length: meses }, (_, i) => { const d = new Date(Date.UTC(ancla.a, ancla.m + i, 1)); return { a: d.getUTCFullYear(), m: d.getUTCMonth() }; });
  const pasado = diaIni && diaIni < hoy;
  const opcH = (v) => HORAS_LAB.includes(v) ? HORAS_LAB : [v, ...HORAS_LAB];

  return (
    <div style={{ border: `1px solid ${COL.borde}`, borderRadius: 12, padding: 10 }}>
      {/* navegacion de meses */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <button type="button" onClick={() => nav(-1)} aria-label="Mes anterior" style={{ ...btnSec, padding: 8, minWidth: 44 }}><ChevronLeft size={18} /></button>
        <span style={{ fontSize: 12, color: COL.grisClaro }}>{esperandoFin ? 'Elige la fecha de fin (o el mismo día)' : 'Elige la fecha de inicio'}</span>
        <button type="button" onClick={() => nav(1)} aria-label="Mes siguiente" style={{ ...btnSec, padding: 8, minWidth: 44 }}><ChevronRight size={18} /></button>
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        {meses2.map(mm => <MesCalendario key={`${mm.a}-${mm.m}`} anio={mm.a} mes={mm.m} diaIni={diaIni} diaFin={diaFin} diasCarga={diasCarga} hoy={hoy} onClickDia={clickDia} />)}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => { onCambio({ diaFin: diaIni }); setEsperandoFin(false); }} style={{ ...btnSec, padding: '8px 12px', fontSize: 12 }}>Un solo día</button>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: COL.grisClaro }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} /> día con carga del técnico</span>
      </div>
      {pasado && <div style={{ marginTop: 8, background: '#fff8ed', border: '1px solid #fde3bd', color: '#8a5a1e', borderRadius: 8, padding: '6px 10px', fontSize: 12 }}>⚠ La fecha de inicio ya pasó. Puedes programarla igual.</div>}

      {/* horas */}
      <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
        <label style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 12, color: COL.grisClaro, marginBottom: 2 }}>Empieza a las</span>
          <select value={horaIni} onChange={e => cambiarHoraIni(e.target.value)} style={{ ...inputStyle, padding: '8px 10px' }}>{opcH(horaIni).map(h => <option key={h} value={h}>{h}</option>)}</select>
        </label>
        <label style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 12, color: COL.grisClaro, marginBottom: 2 }}>Termina a las</span>
          <select value={horaFin} onChange={e => onCambio({ horaFin: e.target.value })} style={{ ...inputStyle, padding: '8px 10px' }}>{opcH(horaFin).map(h => <option key={h} value={h}>{h}</option>)}</select>
        </label>
      </div>

      {/* resumen legible */}
      <div style={{ marginTop: 10, background: '#eaf2fb', borderRadius: 8, padding: '8px 12px', fontSize: 13.5, fontWeight: 600, color: COL.oscuro }}>
        {resumenRango(diaIni, diaFin, horaIni, horaFin) || 'Elige las fechas'}
      </div>
    </div>
  );
}

// ============================================
// Modal "+ Programar"
// ============================================
function ModalProgramar({ tecnicos, esCelular, inicial, onCerrar, onCreada }) {
  const [tipo, setTipo] = useState(inicial.tipo || 'levantamiento');
  const [selTec, setSelTec] = useState(inicial.tecnicos || []);
  // Bloque 3: personas de cuadrillas externas (ids de contratista_integrantes) e instrucciones solo para ellas.
  const [selInt, setSelInt] = useState([]);
  const [externas, setExternas] = useState({ personas: [], puede_programar: false, cargado: false });
  const [instrucciones, setInstrucciones] = useState('');
  const hoy = hoyYMD();
  const initIni = inicial.fecha_inicio ? toLocalInput(inicial.fecha_inicio) : `${hoy}T09:00`;
  const initFin = inicial.fecha_fin ? toLocalInput(inicial.fecha_fin) : `${hoy}T11:00`;
  const [diaIni, setDiaIni] = useState(initIni.slice(0, 10));
  const [diaFin, setDiaFin] = useState(initFin.slice(0, 10));
  const [horaIni, setHoraIni] = useState(initIni.slice(11, 16));
  const [horaFin, setHoraFin] = useState(initFin.slice(11, 16));
  const [oportunidad, setOportunidad] = useState(inicial.oportunidad || null);
  const [seleccionando, setSeleccionando] = useState(false);
  const [progDia, setProgDia] = useState([]);         // trabajos del dia (disponibilidad de tecnicos)
  const [diasCarga, setDiasCarga] = useState(new Set()); // dias con carga del tecnico (pistas del calendario)
  const [rangoVisible, setRangoVisible] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState('');
  // Bloque 3: disponibilidad de la gente externa para el rango elegido (regla dura + empalmes, la calcula el servidor).
  useEffect(() => {
    if (!diaIni || !horaIni || !horaFin) return undefined;
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const ini = fromLocalInput(`${diaIni}T${horaIni}`), fin = fromLocalInput(`${diaFin || diaIni}T${horaFin}`);
        if (new Date(fin) <= new Date(ini)) return;
        const r = await api(`/api/programaciones/disponibles?desde=${encodeURIComponent(ini)}&hasta=${encodeURIComponent(fin)}`);
        if (vivo) { setExternas({ personas: r.personas || [], puede_programar: !!r.puede_programar, cargado: true }); setSelInt(s => s.filter(id => (r.personas || []).some(p => p.id === id && p.disponible))); }
      } catch (e) { if (vivo) setExternas({ personas: [], puede_programar: false, cargado: true }); }
    }, 350);
    return () => { vivo = false; clearTimeout(t); };
  }, [diaIni, diaFin, horaIni, horaFin]);
  // Recurrencia (patron Google Calendar)
  const [freq, setFreq] = useState('no');            // no | semanal | quincenal | mensual | personalizado
  const [diasSemana, setDiasSemana] = useState([]);   // 0=Lun..6=Dom (personalizado)
  const [terminaTipo, setTerminaTipo] = useState('nrep'); // nrep | fecha | nunca
  const [terminaN, setTerminaN] = useState(8);
  const [terminaFecha, setTerminaFecha] = useState('');
  const [decision, setDecision] = useState(null);     // panel de empalmes
  const expedienteFijo = !!inicial.oportunidad_bloqueada;
  const anchoVp = useAnchoViewport();
  const compacto = esCelular || anchoVp < 1100;

  const iniLocal = `${diaIni}T${horaIni}`;
  const finLocal = `${diaFin}T${horaFin}`;
  const aplicarRango = (p) => {
    if (p.diaIni !== undefined) setDiaIni(p.diaIni);
    if (p.diaFin !== undefined) setDiaFin(p.diaFin);
    if (p.horaIni !== undefined) setHoraIni(p.horaIni);
    if (p.horaFin !== undefined) setHoraFin(p.horaFin);
  };
  const onRangoVisible = useCallback((desde, hasta) => setRangoVisible({ desde, hasta }), []);

  // Disponibilidad de tecnicos: trabajos del rango [inicio, fin] (cubre turnos nocturnos que cruzan medianoche).
  useEffect(() => {
    if (!diaIni) return undefined;
    let vivo = true;
    const t = setTimeout(async () => {
      try { const r = await api(`/api/programaciones?desde=${diaIni}T00:00:00${MX_OFFSET}&hasta=${diaFin || diaIni}T23:59:59${MX_OFFSET}`); if (vivo) setProgDia(r.programaciones || []); }
      catch (e) { if (vivo) setProgDia([]); }
    }, 250);
    return () => { vivo = false; clearTimeout(t); };
  }, [diaIni, diaFin]);

  // Pistas del calendario: dias donde el/los tecnico(s) elegido(s) ya tienen carga.
  useEffect(() => {
    if (!rangoVisible || selTec.length === 0) { setDiasCarga(new Set()); return undefined; }
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const r = await api(`/api/programaciones?desde=${rangoVisible.desde}T00:00:00${MX_OFFSET}&hasta=${rangoVisible.hasta}T23:59:59${MX_OFFSET}`);
        const set = new Set();
        (r.programaciones || []).forEach(p => {
          if (p.estado === 'cancelado') return;
          if (!(p.tecnicos || []).some(t => selTec.includes(t.id))) return;
          let d = ymd(new Date(p.fecha_inicio)); const df = ymd(new Date(p.fecha_fin));
          while (d <= df) { set.add(d); d = addDays(d, 1); }
        });
        if (vivo) setDiasCarga(set);
      } catch (e) { if (vivo) setDiasCarga(new Set()); }
    }, 250);
    return () => { vivo = false; clearTimeout(t); };
  }, [rangoVisible, selTec]);

  const reglaSerie = () => ({ freq, dias: freq === 'personalizado' ? diasSemana : undefined, termina: { tipo: terminaTipo, n: terminaN, fecha: terminaFecha } });
  const ocurrencias = () => generarOcurrencias({ diaIni, diaFin, horaIni, horaFin, freq, diasSemana, terminaTipo, terminaN, terminaFecha });

  const enviar = async (lista, recurrencia) => {
    setDecision(null); setGuardando(true); setErr('');
    try {
      await api('/api/programaciones', { method: 'POST', body: JSON.stringify({ tipo, tecnicos: selTec, integrantes: selInt, instrucciones_externo: selInt.length ? instrucciones : undefined, oportunidad_id: oportunidad.id, ocurrencias: lista, recurrencia }) });
      onCreada();
    } catch (e) { setErr(e.message || 'No se pudo crear.'); setGuardando(false); }
  };

  const guardar = async () => {
    if (!oportunidad) { setErr('Selecciona el expediente al que pertenece este trabajo.'); return; }
    if (new Date(fromLocalInput(finLocal)) <= new Date(fromLocalInput(iniLocal))) { setErr('La fecha/hora de fin debe ser posterior al inicio.'); return; }
    if (!selTec.length && !selInt.length) { setErr('Elige al menos una persona: un técnico interno o alguien de una cuadrilla externa.'); return; }
    if (selInt.length && freq !== 'no') { setErr('Las cuadrillas externas se proponen trabajo por trabajo: quita la repetición o quita a la gente externa.'); return; }
    if (freq === 'personalizado' && diasSemana.length === 0) { setErr('Elige al menos un día de la semana para la repetición personalizada.'); return; }
    const esSerie = freq !== 'no';
    const lista = ocurrencias();
    if (!lista.length) { setErr('La regla de repetición no genera ninguna fecha.'); return; }
    if (!esSerie) { enviar(lista, null); return; }
    // Chequeo de empalmes de la SERIE: no crear empalmes silenciosos.
    setGuardando(true); setErr('');
    try {
      const primero = lista[0].fecha_inicio.slice(0, 10);
      const ultimo = lista[lista.length - 1].fecha_fin.slice(0, 10);
      const r = await api(`/api/programaciones?desde=${primero}T00:00:00${MX_OFFSET}&hasta=${ultimo}T23:59:59${MX_OFFSET}`);
      const choca = ocurrenciasQueChocan(lista, r.programaciones || [], selTec);
      setGuardando(false);
      if (choca.length === 0) enviar(lista, reglaSerie());
      else setDecision({ lista, recurrencia: reglaSerie(), choca });
    } catch (e) { setErr(e.message || 'No se pudo verificar empalmes.'); setGuardando(false); }
  };

  return (
    <Overlay onCerrar={onCerrar} esCelular={esCelular} ancho={560}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 18, color: COL.oscuro, flex: 1 }}><Plus size={18} style={{ verticalAlign: 'middle' }} /> Programar trabajo</h3>
        <button onClick={onCerrar} aria-label="Cerrar" style={btnCerrar}><X size={22} /></button>
      </div>
      {err && <div style={{ background: COL.fondoRojo, color: COL.rojo, padding: '8px 12px', borderRadius: 8, marginBottom: 10, fontSize: 13 }}>{err}</div>}

      <label style={lbl}>Tipo de trabajo</label>
      <select value={tipo} onChange={e => setTipo(e.target.value)} style={inputStyle}>
        {Object.entries(TIPOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>

      <label style={lbl}>Expediente <span style={{ color: COL.rojo }}>*</span></label>
      {oportunidad ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 14, color: COL.oscuro, background: '#eaf2fb', border: `1px solid ${COL.borde}`, padding: '10px 12px', borderRadius: 10 }}>
          <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}><b>{oportunidad.folio || `#${oportunidad.id}`}</b>{oportunidad.titulo ? ` · ${oportunidad.titulo}` : ''}{oportunidad.cliente_nombre ? ` — ${oportunidad.cliente_nombre}` : ''}</span>
          {!expedienteFijo && <button type="button" onClick={() => setSeleccionando(true)} style={{ ...btnSec, padding: '10px 14px' }}>Cambiar</button>}
        </div>
      ) : (
        <button type="button" onClick={() => setSeleccionando(true)} style={{ ...btnSec, width: '100%', justifyContent: 'flex-start', gap: 8 }}>
          <FolderOpen size={18} /> Seleccionar expediente…
        </button>
      )}
      <div style={{ fontSize: 11.5, color: COL.grisClaro, marginTop: 4 }}>Todo trabajo se agenda sobre un expediente. Cliente, ubicación y dirección se toman de ahí.</div>

      <label style={lbl}>Fecha y hora</label>
      <RangoFechaHora diaIni={diaIni} diaFin={diaFin} horaIni={horaIni} horaFin={horaFin}
        onCambio={aplicarRango} diasCarga={diasCarga} onRangoVisible={onRangoVisible} compacto={compacto} />

      <label style={lbl}><Repeat size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Se repite</label>
      <select value={freq} onChange={e => setFreq(e.target.value)} style={inputStyle}>
        <option value="no">No se repite</option>
        <option value="semanal">Cada semana</option>
        <option value="quincenal">Cada 2 semanas</option>
        <option value="mensual">Cada mes</option>
        <option value="personalizado">Personalizado (días de la semana)</option>
      </select>
      {freq === 'personalizado' && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
          {DOW_LARGO.map((d, i) => { const on = diasSemana.includes(i); return (
            <button key={i} type="button" onClick={() => setDiasSemana(on ? diasSemana.filter(x => x !== i) : [...diasSemana, i])}
              style={{ border: `1px solid ${on ? COL.principal : COL.borde}`, background: on ? '#eaf2fb' : '#fff', color: on ? COL.oscuro : COL.gris, borderRadius: 8, padding: '8px 8px', minHeight: 44, minWidth: 44, fontSize: 12.5, cursor: 'pointer', fontFamily: PP_EXP }}>{d}</button>
          ); })}
        </div>
      )}
      {freq !== 'no' && (
        <div style={{ marginTop: 8 }}>
          <span style={{ fontSize: 12, color: COL.grisClaro }}>Termina:</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
            <select value={terminaTipo} onChange={e => setTerminaTipo(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 160 }}>
              <option value="nrep">Después de N repeticiones</option>
              <option value="fecha">En una fecha</option>
              <option value="nunca">Nunca (tope 12 meses)</option>
            </select>
            {terminaTipo === 'nrep' && <input type="number" min={1} max={366} value={terminaN} onChange={e => setTerminaN(Number(e.target.value) || 1)} style={{ ...inputStyle, width: 90 }} />}
            {terminaTipo === 'fecha' && <input type="date" value={terminaFecha} onChange={e => setTerminaFecha(e.target.value)} style={{ ...inputStyle, width: 'auto' }} />}
          </div>
          <div style={{ fontSize: 11.5, color: COL.grisClaro, marginTop: 4 }}>Se generarán <b>{ocurrencias().length}</b> trabajos (mismo expediente, técnico(s) y horario).</div>
        </div>
      )}

      <label style={lbl}>Técnico(s) — uno o varios (cuadrilla)</label>
      <SelectorTecnicosDisponibilidad tecnicos={tecnicos} seleccionados={selTec} onCambio={setSelTec} progDia={progDia} ini={iniLocal} fin={finLocal} />

      {/* Bloque 3: cuadrillas externas. Solo aparecen DISPONIBLES los que cumplen la regla dura (documentos obligatorios
          vigentes, estado activo) y no chocan de horario; si alguien no puede, se dice por que. */}
      {externas.cargado && externas.personas.length > 0 && (
        <>
          <label style={lbl}><HardHat size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Cuadrillas externas {externas.puede_programar ? '' : '(solo lectura: hace falta el permiso "Programar servicio")'}</label>
          <SelectorExternos personas={externas.personas} seleccionados={selInt} onCambio={setSelInt} bloqueado={!externas.puede_programar} />
          {selInt.length > 0 && (
            <>
              <label style={lbl}>Instrucciones para la cuadrilla externa (lo ÚNICO que verán además de fecha, sitio y contacto)</label>
              <textarea value={instrucciones} onChange={e => setInstrucciones(e.target.value)} rows={3} placeholder="Ej. Llevar escalera de 6 m. Reportarse con el guardia de la caseta 2." style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }} />
              <div style={{ fontSize: 11.5, color: COL.grisClaro, marginTop: 4 }}>Cada persona recibe la propuesta en su portal y tiene 24 h para aceptar o rechazar (con motivo). Nunca ven el expediente ni el dinero.</div>
            </>
          )}
        </>
      )}

      {decision && (
        <div style={{ background: '#fff8ed', border: '1px solid #fde3bd', borderRadius: 10, padding: 12, marginTop: 12 }}>
          <div style={{ fontSize: 13.5, color: '#8a5a1e', fontWeight: 600, marginBottom: 8 }}>⚠ {decision.choca.length} de {decision.lista.length} fechas empalman con otro trabajo del técnico. Esas no se pueden crear (un técnico no puede estar en dos lugares a la vez).</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {decision.lista.length - decision.choca.length > 0 && <button disabled={guardando} onClick={() => enviar(decision.lista.filter(o => !decision.choca.includes(o)), decision.recurrencia)} style={btnPrimario}>Crear las {decision.lista.length - decision.choca.length} que sí caben</button>}
            <button onClick={() => setDecision(null)} style={btnSec}>Cambiar horario</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        <button disabled={guardando || !oportunidad} onClick={guardar} style={{ ...btnPrimario, opacity: (guardando || !oportunidad) ? 0.6 : 1 }}>{guardando ? 'Guardando…' : (freq !== 'no' ? `Programar ${ocurrencias().length}` : 'Programar')}</button>
        <button onClick={onCerrar} style={btnSec}>Cancelar</button>
      </div>

      {/* Overlay de SELECCION: la pantalla REAL de Expedientes en modo seleccion,
          encima del modal (que queda montado -> no se pierde el estado capturado). */}
      {seleccionando && (
        <div style={{ position: 'fixed', inset: 0, background: '#f3f4f4', zIndex: 1500, overflowY: 'auto' }}>
          <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: COL.grisClaro, fontFamily: PP_EXP }}>Cargando expedientes…</div>}>
            <ExpedientesLazy
              esCelular={esCelular}
              modoSeleccion
              etiquetaSeleccion="Elige el expediente para este trabajo del calendario."
              onElegir={(el) => { setOportunidad({ id: el.id, folio: el.folio, titulo: el.proyecto, cliente_nombre: el.cliente }); setSeleccionando(false); }}
              onCancelarSeleccion={() => setSeleccionando(false)}
            />
          </Suspense>
        </div>
      )}
    </Overlay>
  );
}

// Bloque 3: gente de cuadrillas externas, agrupada por contratista, con disponibilidad y MOTIVO cuando no se puede.
function SelectorExternos({ personas, seleccionados, onCambio, bloqueado }) {
  const toggle = (id) => onCambio(seleccionados.includes(id) ? seleccionados.filter(x => x !== id) : [...seleccionados, id]);
  const grupos = useMemo(() => { const m = new Map(); personas.forEach(p => { if (!m.has(p.contratista)) m.set(p.contratista, []); m.get(p.contratista).push(p); }); return [...m.entries()]; }, [personas]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto', border: `1px solid ${COL.borde}`, borderRadius: 10, padding: 6 }}>
      {grupos.map(([contratista, lista]) => (
        <div key={contratista}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: COL.grisClaro, textTransform: 'uppercase', letterSpacing: 0.4, padding: '4px 6px' }}>{contratista}</div>
          {lista.map(p => {
            const on = seleccionados.includes(p.id);
            const noPuede = !p.disponible || bloqueado;
            return (
              <button key={p.id} type="button" disabled={noPuede && !on} onClick={() => { if (!noPuede || on) toggle(p.id); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%', minHeight: 52, padding: '8px 10px', borderRadius: 10, cursor: noPuede ? 'not-allowed' : 'pointer', fontFamily: PP_EXP, border: `1px solid ${on ? COL.principal : (noPuede ? '#f3c4c4' : COL.borde)}`, background: noPuede ? '#fdf0f0' : (on ? '#eaf2fb' : '#fff'), opacity: noPuede ? 0.8 : 1, marginBottom: 4 }}>
                <span style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${on ? COL.principal : COL.grisClaro}`, background: on ? COL.principal : '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{on && <Check size={14} color="#fff" />}</span>
                <span style={{ width: 34, height: 34, borderRadius: '50%', background: '#8b5cf6', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{iniciales(p.nombre)}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: COL.gris, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nombre}</span>
                  <span style={{ display: 'block', fontSize: 12, color: p.disponible ? '#1D9E75' : COL.rojo, fontWeight: 600, marginTop: 2, lineHeight: 1.3 }}>{p.disponible ? 'Disponible' : (p.motivos || []).map(m => m.texto).join(' · ')}</span>
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// Lista de tecnicos con avatar y DISPONIBILIDAD para la fecha/hora elegida.
function SelectorTecnicosDisponibilidad({ tecnicos, seleccionados, onCambio, progDia, ini, fin }) {
  const [busca, setBusca] = useState('');
  const toggle = (id) => onCambio(seleccionados.includes(id) ? seleccionados.filter(x => x !== id) : [...seleccionados, id]);

  const filas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return tecnicos
      .filter(t => !q || String(t.nombre || '').toLowerCase().includes(q))
      .map(t => ({ ...t, disp: disponibilidadTec(t.id, progDia, ini, fin) }))
      .sort((a, b) => (a.disp.orden - b.disp.orden) || String(a.nombre).localeCompare(String(b.nombre)));
  }, [tecnicos, busca, progDia, ini, fin]);

  const nombresSel = tecnicos.filter(t => seleccionados.includes(t.id)).map(t => t.nombre);

  return (
    <div>
      {tecnicos.length > 6 && (
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar técnico…"
          style={{ ...inputStyle, marginBottom: 6 }} />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto', border: `1px solid ${COL.borde}`, borderRadius: 10, padding: 6 }}>
        {tecnicos.length === 0 && <span style={{ fontSize: 13, color: COL.grisClaro, padding: 8 }}>No hay técnicos registrados.</span>}
        {filas.map(t => {
          const on = seleccionados.includes(t.id);
          const bloqueado = t.disp.nivel === 'rojo' && !on; // empalme -> no seleccionable (candado)
          return (
            <button key={t.id} type="button" disabled={bloqueado} onClick={() => { if (!bloqueado) toggle(t.id); }}
              title={bloqueado ? t.disp.texto : undefined}
              style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%', minHeight: 52, padding: '8px 10px', borderRadius: 10, cursor: bloqueado ? 'not-allowed' : 'pointer', fontFamily: PP_EXP, border: `1px solid ${on ? COL.principal : (bloqueado ? '#f3c4c4' : COL.borde)}`, background: bloqueado ? '#fdf0f0' : (on ? '#eaf2fb' : '#fff'), opacity: bloqueado ? 0.75 : 1 }}>
              <span style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${on ? COL.principal : COL.grisClaro}`, background: on ? COL.principal : '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {on && <Check size={14} color="#fff" />}
              </span>
              <span style={{ width: 34, height: 34, borderRadius: '50%', background: colorDe(t.nombre), color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{iniciales(t.nombre)}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, color: COL.gris }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{t.nombre}</span>
                  {t.externo && <span style={{ flexShrink: 0, fontSize: 10, background: '#f3efff', color: '#8b5cf6', padding: '1px 6px', borderRadius: 6 }}>Externo</span>}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: t.disp.color, fontWeight: 600, marginTop: 2 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.disp.color, display: 'inline-block' }} /> {t.disp.texto}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      {nombresSel.length > 0 && (
        <div style={{ fontSize: 12.5, color: COL.gris, marginTop: 6 }}><b>Seleccionados:</b> {nombresSel.join(', ')}</div>
      )}
    </div>
  );
}

// ============================================
// Programar DESDE el expediente (se usa en el paso Levantamiento y otros)
// Auto-carga la lista de tecnicos y abre el modal con el expediente ya ligado.
// ============================================
export function ProgramarDesdeExpediente({ oportunidad, tipoSugerido, esCelular, onCerrar, onCreada }) {
  const [tecnicos, setTecnicos] = useState([]);
  const [estado, setEstado] = useState('cargando'); // cargando | listo | sinAcceso | error
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await api('/api/programaciones/tecnicos');
        if (vivo) { setTecnicos(r.tecnicos || []); setEstado('listo'); }
      } catch (e) {
        if (!vivo) return;
        if (/403/.test(e.message) || /acceso/i.test(e.message)) { setEstado('sinAcceso'); }
        else { setEstado('error'); setMsg(e.message); }
      }
    })();
    return () => { vivo = false; };
  }, []);

  if (estado === 'cargando') {
    return <Overlay onCerrar={onCerrar} esCelular={esCelular}><div style={{ padding: 20, textAlign: 'center', color: COL.grisClaro }}>Cargando…</div></Overlay>;
  }
  if (estado === 'sinAcceso') {
    return (
      <Overlay onCerrar={onCerrar} esCelular={esCelular}>
        <h3 style={{ margin: '0 0 8px', fontSize: 17, color: COL.oscuro }}>Programar visita</h3>
        <p style={{ fontSize: 14, color: COL.gris, lineHeight: 1.5 }}>La programación de campo la gestiona dirección/operaciones desde el calendario. Avisa a tu coordinador para que agende esta visita.</p>
        <button onClick={onCerrar} style={btnPrimario}>Entendido</button>
      </Overlay>
    );
  }
  if (estado === 'error') {
    return (
      <Overlay onCerrar={onCerrar} esCelular={esCelular}>
        <p style={{ fontSize: 14, color: COL.rojo }}>{msg || 'No se pudo abrir la programación.'}</p>
        <button onClick={onCerrar} style={btnSec}>Cerrar</button>
      </Overlay>
    );
  }
  return (
    <ModalProgramar
      tecnicos={tecnicos} esCelular={esCelular}
      inicial={{
        tipo: tipoSugerido || 'levantamiento',
        oportunidad: oportunidad || null,
        oportunidad_bloqueada: !!oportunidad,
      }}
      onCerrar={onCerrar}
      onCreada={onCreada}
    />
  );
}

// ---- Overlay reutilizable (cierra con clic fuera y con Escape) ----
function Overlay({ children, onCerrar, esCelular, ancho }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCerrar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCerrar]);
  return (
    <div className="cal-modal" onClick={onCerrar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: esCelular ? 'flex-end' : 'center', justifyContent: 'center', padding: esCelular ? 0 : 20 }}>
      <style>{'.cal-modal button{transition:background 160ms ease,filter 160ms ease,opacity 160ms ease}.cal-modal button:hover{filter:brightness(0.95)}'}</style>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: esCelular ? '16px 16px 0 0' : 16, padding: esCelular ? 18 : 24, width: esCelular ? '100%' : (ancho || 460), maxWidth: '100%', maxHeight: esCelular ? '92vh' : '88vh', overflowY: 'auto', fontFamily: PP_EXP, boxSizing: 'border-box' }}>
        {children}
      </div>
    </div>
  );
}
